import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, Select, Alert, Spin, Button } from 'antd';
import { ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { IBcfImportSettings } from 'Data/BcfImport.api';

interface IStep3SettingsProps {
  settings: IBcfImportSettings;
  isLoading: boolean;
  isLoadingDefaults: boolean;
  defaultsError: string | undefined;
  error: string | undefined;
  availableDefaults: any;
  onSettingsChange: (settings: Partial<IBcfImportSettings>) => void;
  onRetryLoadDefaults?: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const Description = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const SettingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 4px;
`;

const SettingLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  cursor: pointer;
  flex: 1;
  margin: 0;
`;

const SettingDescription = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0 0 0 20px;
`;

const SelectorGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 4px;
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SelectorLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

const RequiredMark = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const OptionalMark = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin-left: 4px;
`;

const RequiredNote = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
  font-style: italic;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 16px;
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 8px;
`;

const Step3Settings: FC<IStep3SettingsProps> = ({

  settings,
  isLoading,
  isLoadingDefaults,
  defaultsError,
  error,
  availableDefaults,
  onSettingsChange,
  onRetryLoadDefaults,
}) => {
  const { t } = useTranslation('activities');

  // Show loading state when fetching defaults
  if (isLoadingDefaults) {
    return (
      <Container>
        <Header>
          <Title>Configurações de Importação</Title>
          <Description>Carregando dados disponíveis...</Description>
        </Header>
        <LoadingContainer>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
          <LoadingText>Buscando disciplinas, usuários e etapas...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  // Show error state with retry button
  if (defaultsError && !availableDefaults) {
    return (
      <Container>
        <Header>
          <Title>Configurações de Importação</Title>
          <Description>Defina as opções para a importação do BCF</Description>
        </Header>
        <ErrorContainer>
          <Alert
            message="Erro ao carregar dados"
            description={defaultsError}
            type="error"
            showIcon
          />
          {onRetryLoadDefaults && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={onRetryLoadDefaults}
            >
              Tentar Novamente
            </Button>
          )}
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Configurações de Importação</Title>
        <Description>Defina as opções para a importação do BCF</Description>
      </Header>

      {error && <Alert message={error} type="error" showIcon />}

      <SettingGroup>
        <SettingRow>
          <Checkbox
            checked={settings.updateExistingIssues}
            onChange={(e) => onSettingsChange({ updateExistingIssues: e.target.checked })}
          />
          <div style={{ flex: 1 }}>
            <SettingLabel>Atualizar Problemas Existentes</SettingLabel>
            <SettingDescription>Atualizar issues que já existem na plataforma</SettingDescription>
          </div>
        </SettingRow>

        <SettingRow>
          <Checkbox
            checked={settings.createNewActivities}
            onChange={(e) => onSettingsChange({ createNewActivities: e.target.checked })}
          />
          <div style={{ flex: 1 }}>
            <SettingLabel>{t('bcfImport.createNewIssues')}</SettingLabel>
            <SettingDescription>{t('bcfImport.createIssuesDescription')}</SettingDescription>
          </div>
        </SettingRow>

        <SettingRow>
          <Checkbox
            checked={settings.attachFiles}
            onChange={(e) => onSettingsChange({ attachFiles: e.target.checked })}
          />
          <div style={{ flex: 1 }}>
            <SettingLabel>Anexar Arquivos</SettingLabel>
            <SettingDescription>{t('bcfImport.attachFilesDescription')}</SettingDescription>
          </div>
        </SettingRow>

        <SettingRow>
          <Checkbox
            checked={settings.skipUnmappedItems}
            onChange={(e) => onSettingsChange({ skipUnmappedItems: e.target.checked })}
          />
          <div style={{ flex: 1 }}>
            <SettingLabel>Pular Itens Não Mapeados</SettingLabel>
            <SettingDescription>Ignorar valores do BCF que não foram mapeados</SettingDescription>
          </div>
        </SettingRow>
      </SettingGroup>

      <SelectorGroup>
        <SelectorContainer>
          <SelectorLabel>
            Atribuir Padrão a
            {!settings.skipUnmappedItems && <RequiredMark>*</RequiredMark>}
            {settings.skipUnmappedItems && <OptionalMark>(opcional)</OptionalMark>}
          </SelectorLabel>
          <Select
            placeholder="Selecione um usuário"
            value={settings.defaultAssignee || undefined}
            onChange={(value) => onSettingsChange({ defaultAssignee: value })}
            loading={isLoading}
            disabled={settings.skipUnmappedItems}
            options={(availableDefaults?.Users || []).map((user: any) => ({
              label: user.FullName || user.Name || user.Email,
              value: user.ApplicationUserId || user.Id,
            }))}
          />
          {!settings.skipUnmappedItems && (
            <RequiredNote>
              Obrigatório — ou ative "Pular Itens Não Mapeados" acima
            </RequiredNote>
          )}
        </SelectorContainer>

        <SelectorContainer>
          <SelectorLabel>
            Etapa Padrão
            <OptionalMark>(opcional)</OptionalMark>
          </SelectorLabel>
          <Select
            placeholder="Selecione uma etapa"
            value={settings.defaultStage || undefined}
            onChange={(value) => onSettingsChange({ defaultStage: value })}
            loading={isLoading}
            options={(availableDefaults?.TopicStages || []).map((stage: any) => ({
              label: stage.Name,
              value: stage.TopicStageId,
            }))}
          />
        </SelectorContainer>
      </SelectorGroup>

      <SelectorGroup>
        <SelectorContainer>
          <SelectorLabel>
            Disciplina Padrão
            <OptionalMark>(opcional)</OptionalMark>
          </SelectorLabel>
          <Select
            placeholder="Selecione uma disciplina"
            value={settings.defaultDisciplineId || undefined}
            onChange={(value) => onSettingsChange({ defaultDisciplineId: value })}
            loading={isLoading}
            options={(availableDefaults?.ConstructionSiteDisciplines || []).map((discipline: any) => ({
              label: discipline.CustomName || discipline.Discipline?.Name || `Disciplina ${discipline.ConstructionSiteDisciplinesId}`,
              value: discipline.ConstructionSiteDisciplinesId,
            }))}
          />
          <SettingDescription style={{ marginLeft: 0 }}>Disciplina padrão para tópicos importados</SettingDescription>
        </SelectorContainer>

        <SelectorContainer>
          <SelectorLabel>
            Ponto de Localização
            <OptionalMark>(opcional)</OptionalMark>
          </SelectorLabel>
          <Select
            placeholder="Digite ou selecione um ponto"
            value={settings.defaultPointName || undefined}
            onChange={(value) => onSettingsChange({ defaultPointName: value })}
            loading={isLoading}
            showSearch
            allowClear
            options={(availableDefaults?.TopicPoints || []).map((point: any) => ({
              label: point.Name,
              value: point.Name,
            }))}
            onSearch={(value) => {
              if (value) {
                onSettingsChange({ defaultPointName: value });
              }
            }}
          />
          <SettingDescription style={{ marginLeft: 0 }}>Ponto de localização para tópicos importados</SettingDescription>
        </SelectorContainer>
      </SelectorGroup>
    </Container>
  );
};

export default Step3Settings;
